import { useCallback, useEffect, useState, type ReactNode } from "react";
import type { Tier } from "@/lib/tier";
import {
  TIER_OVERRIDE_STORAGE_KEY,
  TierOverrideContext,
  VALID_OVERRIDE_TIERS,
} from "./useTierOverride";

export const TierOverrideProvider = ({ children }: { children: ReactNode }) => {
  const [override, setOverrideState] = useState<Tier | null>(() => {
    try {
      const raw = localStorage.getItem(TIER_OVERRIDE_STORAGE_KEY);
      if (raw && (VALID_OVERRIDE_TIERS as string[]).includes(raw)) return raw as Tier;
    } catch { /* ignore */ }
    return null;
  });

  const setOverride = useCallback((tier: Tier | null) => {
    setOverrideState(tier);
    try {
      if (tier) localStorage.setItem(TIER_OVERRIDE_STORAGE_KEY, tier);
      else localStorage.removeItem(TIER_OVERRIDE_STORAGE_KEY);
    } catch { /* ignore */ }
  }, []);

  const clearOverride = useCallback(() => setOverride(null), [setOverride]);

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== TIER_OVERRIDE_STORAGE_KEY) return;
      const v = e.newValue;
      setOverrideState(v && (VALID_OVERRIDE_TIERS as string[]).includes(v) ? (v as Tier) : null);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <TierOverrideContext.Provider value={{ override, setOverride, clearOverride }}>
      {children}
    </TierOverrideContext.Provider>
  );
};
